import { use } from 'react';
import { UNSAFE_DataRouterContext, matchPath, useLocation } from 'react-router';
import styled from 'styled-components';

import { catchAll } from '@sorare/routing';

import { Sport } from '__generated__/globalTypes';
import { Dropdown } from 'atoms/dropdowns/Dropdown';
import { FootballLogo } from 'atoms/icons/FootballLogo';
import { MLBLogo } from 'atoms/icons/MLBLogo';
import { NBALogo } from 'atoms/icons/NBALogo';
import { Vertical } from 'atoms/layout/flex';
import { BrandM } from 'atoms/typography';
import { ANY_SPORT_MY_CLUB } from 'constants/__generated__/routes';
import { useSportContext } from 'contexts/sport';
import {
  SPORT_PATH_MAP,
  generateSportPath,
  matchAnySportPath,
} from 'lib/routing/generateSportPath';
import { getPathPattern } from 'lib/routing/getPathPattern';

import { SportButtonContent } from './SportButtonContent';
import { SportLink } from './SportLink';

const DropdownContent = styled(Vertical)`
  padding: var(--double-unit);
  background-color: var(--c-black);
`;

const isMyClub = (pathname: string) => {
  return matchAnySportPath(catchAll(ANY_SPORT_MY_CLUB), pathname);
};

const useGetSwitchSportPath = () => {
  const { pathname } = useLocation();
  const { sport: currentSport } = useSportContext();

  const isSportPath = Object.values(SPORT_PATH_MAP).some(path =>
    matchPath(catchAll(path), pathname)
  );

  const { router } = use(UNSAFE_DataRouterContext) ?? {};

  const pathPattern = getPathPattern(router?.routes ?? [], pathname);
  const pathWithoutParam = pathPattern.split(':').at(0);

  return (sport: Sport) => {
    if (isSportPath && sport !== currentSport && pathWithoutParam) {
      const pathWithoutParamAndSport = matchPath(
        catchAll(SPORT_PATH_MAP[currentSport]),
        pathWithoutParam
      )?.params['*'];

      // keeps the pathname params if it's my club to avoid redirecting back to current user's my club
      if (isMyClub(pathname)) {
        const pathWithoutSport = matchPath(
          catchAll(SPORT_PATH_MAP[currentSport]),
          pathname
        )?.params['*'];

        if (pathWithoutSport) {
          return generateSportPath(pathWithoutSport, { sport });
        }
      }
      if (pathWithoutParamAndSport) {
        return generateSportPath(pathWithoutParamAndSport, { sport });
      }
    }
    return SPORT_PATH_MAP[sport];
  };
};

type Props = {
  displaySelectedSportName?: boolean;
  withoutChevronIcon?: boolean;
};
export const SportSelector = ({
  displaySelectedSportName,
  withoutChevronIcon,
}: Props) => {
  const getSwitchSportPath = useGetSwitchSportPath();
  return (
    <Dropdown
      label={({ onClick, open }) => (
        <button type="button" onClick={onClick}>
          <SportButtonContent
            displaySelectedSportName={displaySelectedSportName}
            withoutChevronIcon={withoutChevronIcon}
            active={open}
          />
        </button>
      )}
    >
      {({ closeDropdown }) => (
        <DropdownContent>
          <SportLink
            to={getSwitchSportPath(Sport.FOOTBALL)}
            icon={<FootballLogo width={45} height={45} />}
            title={<BrandM bold>Football</BrandM>}
            onClick={closeDropdown}
          />
          <SportLink
            to={getSwitchSportPath(Sport.NBA)}
            icon={<NBALogo width={45} height={45} />}
            title={<BrandM bold>NBA</BrandM>}
            onClick={closeDropdown}
          />
          <SportLink
            to={getSwitchSportPath(Sport.BASEBALL)}
            icon={<MLBLogo width={45} height={45} />}
            title={<BrandM bold>MLB</BrandM>}
            onClick={closeDropdown}
          />
        </DropdownContent>
      )}
    </Dropdown>
  );
};
